import { Fragment, type ReactNode } from "react";

/** A deliberately small Markdown subset for authored UI help, never HTML. */
function inline(text: string): ReactNode {
  return text.split(/(\*\*[^*\n]+\*\*|`[^`\n]+`)/g).map((part, index) =>
    /^\*\*[^*\n]+\*\*$/.test(part)
      ? <strong key={index}>{part.slice(2, -2)}</strong>
      : /^`[^`\n]+`$/.test(part)
        ? <code key={index}>{part.slice(1, -1)}</code>
        : <Fragment key={index}>{part}</Fragment>,
  );
}

/** Supports paragraphs, **bold**, `code`, and flat - / 1. lists in copy JSON.
 * JSX children remain unchanged. Links and HTML are intentionally plain text.
 */
export function HelpText({ children }: { children: ReactNode }) {
  if (typeof children !== "string") return <>{children}</>;
  const blocks: ReactNode[] = [];
  const lines = children.replace(/\r\n?/g, "\n").trim().split("\n");
  for (let i = 0; i < lines.length;) {
    if (!lines[i].trim()) { i++; continue; }
    const bullet = /^\s*[-*] +(.+)$/;
    const numbered = /^\s*\d+\. +(.+)$/;
    const listPattern = bullet.test(lines[i]) ? bullet : numbered.test(lines[i]) ? numbered : null;
    if (listPattern) {
      const start = Number.parseInt(lines[i], 10);
      const items: ReactNode[] = [];
      while (i < lines.length) {
        const match = lines[i].match(listPattern);
        if (!match) break;
        items.push(<li key={i}>{inline(match[1])}</li>);
        i++;
      }
      blocks.push(listPattern === bullet ? <ul key={i}>{items}</ul> : <ol key={i} start={start}>{items}</ol>);
    } else {
      const paragraph: string[] = [];
      do { paragraph.push(lines[i++].trim()); }
      while (i < lines.length && lines[i].trim() && !bullet.test(lines[i]) && !numbered.test(lines[i]));
      blocks.push(<p key={i}>{inline(paragraph.join(" "))}</p>);
    }
  }
  return <div className="help-text">{blocks}</div>;
}
